import { useEffect, useState } from 'react';

import { Container, Row, Col, Button, Card, Collapse, Form, Modal } from 'react-bootstrap';
import { useApp } from 'common/AppContext';

import { PlaywrightExportModal } from './PlaywrightExportModal';
import { SaveToTestRailModal } from './SaveToTestRailModal';
import { RunNotesModal } from './RunNotesModal';
import { StepLog } from './StepLog';

// Pull the hero tints + accents from Bootstrap 5.3 CSS variables so the
// gmetrix bootswatch theme drives them. Hardcoding hex would lock the heroes
// to default-Bootstrap colors regardless of the active theme.
const STATUS_THEME = {
    cancelled: { accent: 'var(--bs-warning-text-emphasis)', glyph: '⏸', label: 'Cancelled', tint: 'var(--bs-warning-bg-subtle)', variant: 'warning' },
    completed: { accent: 'var(--bs-info-text-emphasis)', glyph: '✓', label: 'Completed', tint: 'var(--bs-info-bg-subtle)', variant: 'info' },
    disconnected: { accent: 'var(--bs-secondary-text-emphasis)', glyph: '⚠', label: 'Disconnected', tint: 'var(--bs-secondary-bg-subtle)', variant: 'secondary' },
    failed: { accent: 'var(--bs-danger-text-emphasis)', glyph: '✗', label: 'Failed', tint: 'var(--bs-danger-bg-subtle)', variant: 'danger' },
    passed: { accent: 'var(--bs-success-text-emphasis)', glyph: '✓', label: 'Passed', tint: 'var(--bs-success-bg-subtle)', variant: 'success' },
    ws_error: { accent: 'var(--bs-secondary-text-emphasis)', glyph: '⚠', label: 'Connection error', tint: 'var(--bs-secondary-bg-subtle)', variant: 'secondary' }
};

function formatDuration(startedAt, endedAt) {

    if (!startedAt || !endedAt) return null;

    const ms = Math.max(0, endedAt - startedAt);
    const totalSec = Math.round(ms / 1000);

    if (totalSec < 60) return `${totalSec}s`;

    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return s ? `${m}m ${s}s` : `${m}m`;

}

function countByStatus(steps, name) {

    return (steps || []).filter(s => s.status === name).length;

}

const fmtIso = ts => (ts ? new Date(ts).toISOString() : 'n/a');
const fmtNum = n => (n == null ? 'n/a' : Number(n).toLocaleString());

const truncate = (val, max = 600) => {

    if (val == null) return '(none)';
    let s;
    try {

        s = typeof val === 'string' ? val : JSON.stringify(val);

    } catch (_) {

        s = String(val);

    }
    return s.length > max ? `${s.slice(0, max)}… [truncated, ${s.length - max} more chars]` : s;

};

/**
 * Build the full plaintext debug dump for the results page. Joins everything
 * we know about the run — brief, mode, env primer match, every step's tool +
 * input + result + error, and (if backend supplied them) token usage totals.
 * Intentionally plain text so it pastes cleanly into anywhere — chat, ticket,
 * email.
 */
function formatDebugBundle({
    debug, envContext, freeform, phases, runEndedAt, runStartedAt,
    runSummary, runUuid, status, steps, testPlan, url
}) {

    const lines = [];
    const push = s => lines.push(s == null ? '' : String(s));

    push('=== TEST RUN DEBUG BUNDLE ===');
    push(`run uuid:    ${runUuid || 'n/a'}`);
    push(`status:      ${status || 'n/a'}`);
    push(`mode:        ${freeform ? 'FREEFORM' : 'PHASED'}`);
    push(`target url:  ${url || 'n/a'}`);
    push(`started:     ${fmtIso(runStartedAt)}`);
    push(`ended:       ${fmtIso(runEndedAt)}`);
    if (runStartedAt && runEndedAt)
        push(`duration:    ${formatDuration(runStartedAt, runEndedAt)}`);
    push('');

    push('--- ENV CONTEXT ---');
    if (envContext) {

        push(`matched app: ${envContext.appName || 'n/a'}`);
        push(`matched env: ${envContext.envName || 'n/a'} (id=${envContext.envId})`);

    } else if (debug && debug.envContext) {

        const ec = debug.envContext;
        push(`matched app: ${ec.app ? ec.app.name : 'none'} (${ec.appContextChars || 0} ctx chars, ${ec.appSitemapChars || 0} sitemap chars injected)`);
        push(`matched env: ${ec.env.name} → ${ec.env.url}`);

    } else {

        push('(no environment matched the target URL — orchestrator used the default system prompt only)');

    }
    push('');

    push('--- BRIEF ---');
    if (Array.isArray(phases) && phases.length) {

        phases.forEach((p, i) => {

            push(`Phase ${i} — [${p.category}] ${p.label}`);
            (p.steps || []).forEach((s, j) => push(`  ${j}. ${s}`));

        });

    } else if (testPlan) {

        push(testPlan);

    } else {

        push('(empty)');

    }
    push('');

    push('--- TOKEN USAGE ---');
    if (debug && debug.tokenTotals) {

        const t = debug.tokenTotals;
        push(`bedrock calls: ${fmtNum(t.calls)}`);
        push(`input tokens:  ${fmtNum(t.input_tokens)}`);
        push(`output tokens: ${fmtNum(t.output_tokens)}`);
        push(`cache read:    ${fmtNum(t.cache_read_input_tokens)}`);
        push(`cache write:   ${fmtNum(t.cache_write_input_tokens)}`);

    } else {

        push('(not yet loaded)');

    }
    push('');

    push(`--- STEPS (${(steps || []).length}) ---`);
    (steps || []).forEach((s, i) => {

        const head = `[${i}] ${s.toolName || '?'} — ${s.status || '?'}`;
        const phaseTag = (s.phase_index != null) ? `phase=${s.phase_index} step=${s.step_index}` : '';
        push(phaseTag ? `${head} (${phaseTag})` : head);
        if (s.reasoning && s.reasoning.trim())
            push(`  reasoning: ${truncate(s.reasoning, 400)}`);
        if (s.toolInput && Object.keys(s.toolInput).length)
            push(`  input:     ${truncate(s.toolInput, 400)}`);
        if (s.error)
            push(`  ERROR:     ${truncate(s.error, 400)}`);
        if (s.resultSummary)
            push(`  result:    ${truncate(s.resultSummary, 300)}`);
        push('');

    });

    if (runSummary) {

        push('--- RUN SUMMARY (from finish tool) ---');
        push(runSummary);
        push('');

    }

    return lines.join('\n');

}

/**
 * Render the inline portion of a markdown line: **bold**, *italic*, `code`.
 * Returns an array of strings + JSX nodes.
 */
function renderInline(text) {

    if (!text) return null;

    const parts = text.split(/(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/);

    return parts.map((part, i) => {

        if (/^\*\*[^*]+\*\*$/.test(part))
            return (
                <strong key={i}>
                    {part.slice(2, -2)}
                </strong>
            );

        if (/^\*[^*]+\*$/.test(part))
            return (
                <em key={i}>
                    {part.slice(1, -1)}
                </em>
            );

        if (/^`[^`]+`$/.test(part))
            return (
                <code key={i}>
                    {part.slice(1, -1)}
                </code>
            );

        return part;

    });

}

/**
 * Render an ATX-style markdown heading. Demoted by one level so #/##/### land
 * at h2/h3/h4 — h1 is reserved for the page hero. Visual size constrained via
 * Bootstrap font-size utilities so deeply nested headings don't dwarf the body.
 */
function Heading({ children, level }) {

    const Tag = `h${Math.min(level + 1, 6)}`;
    const sizeClass = level === 1 ? 'fs-4' : level === 2 ? 'fs-5' : 'fs-6';
    return (
        <Tag className={`${sizeClass} fw-bold mt-3 mb-2`}>
            {children}
        </Tag>
    );

}

/**
 * Render the LLM's finish summary as proper HTML. Handles paragraphs, bullet
 * lists (- or *), ATX headings (# through ######), and inline emphasis.
 */
function renderSummary(text) {

    if (!text) return null;

    const blocks = text.split(/\n\s*\n/);

    return blocks.map((block, i) => {

        const lines = block.split('\n').filter(l => l.length);
        if (!lines.length) return null;

        const elements = [];
        let listBuffer = [];

        const flushList = () => {

            if (!listBuffer.length) return;

            elements.push(
                <ul className="mb-2 ps-4" key={`l-${elements.length}`}>
                    {listBuffer.map((item, j) => (
                        <li key={j}>
                            {renderInline(item)}
                        </li>
                    ))}
                </ul>
            );
            listBuffer = [];

        };

        for (const line of lines) {

            const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
            if (headingMatch) {

                flushList();
                elements.push(
                    <Heading key={`h-${elements.length}`} level={headingMatch[1].length}>
                        {renderInline(headingMatch[2])}
                    </Heading>
                );
                continue;

            }

            const listMatch = line.match(/^\s*[-*]\s+(.*)$/);
            if (listMatch) {

                listBuffer.push(listMatch[1]);
                continue;

            }

            flushList();
            elements.push(
                <p className="mb-2" key={`p-${elements.length}`}>
                    {renderInline(line)}
                </p>
            );

        }

        flushList();

        return (
            <div key={i}>
                {elements}
            </div>
        );

    });

}

function EvidenceGallery({ evidence, phases }) {

    const [lightbox, setLightbox] = useState(null);

    if (!Array.isArray(evidence) || evidence.length === 0) return null;

    const close = () => setLightbox(null);

    return (

        <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="px-4 py-3">

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="text-muted small text-uppercase" style={{ letterSpacing: 0.5 }}>
                        Evidence (
                        {evidence.length}
                        )
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                        Captured at key checkpoints by the AI. Click any image to enlarge.
                    </div>
                </div>

                <Row className="g-3">
                    {evidence.map((ev, i) => {

                        const phase = Array.isArray(phases) && ev.phase_index != null ? phases[ev.phase_index] : null;

                        return (

                            <Col key={i} md={4} sm={6} xs={12}>

                                <Card
                                    className="h-100 border"
                                    onClick={() => setLightbox(ev)}
                                    style={{ cursor: 'pointer', overflow: 'hidden' }}
                                >

                                    <div
                                        style={{
                                            background: '#f5f5f5',
                                            borderBottom: '1px solid #e5e5e5',
                                            height: 160,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <img
                                            style={{
                                                display: 'block',
                                                height: '100%',
                                                objectFit: 'cover',
                                                objectPosition: 'top center',
                                                width: '100%'
                                            }}
                                            alt={ev.label}
                                            src={`data:image/png;base64,${ev.image_base64}`}
                                        />
                                    </div>

                                    <Card.Body className="p-2">
                                        <div className="fw-semibold text-truncate" style={{ fontSize: 13 }}>
                                            {ev.label}
                                        </div>
                                        {phase && (
                                            <div className="text-muted text-truncate" style={{ fontSize: 11, marginTop: 2 }}>
                                                {phase.category}
                                                :
                                                {phase.label}
                                            </div>
                                        )}
                                    </Card.Body>

                                </Card>

                            </Col>

                        );

                    })}
                </Row>

            </Card.Body>

            <Modal centered onHide={close} show={!!lightbox} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 18 }}>
                        {lightbox?.label}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-0" style={{ background: '#1e1e1e' }}>
                    {lightbox && (
                        <img
                            alt={lightbox.label}
                            src={`data:image/png;base64,${lightbox.image_base64}`}
                            style={{ display: 'block', margin: '0 auto', maxWidth: '100%' }}
                        />
                    )}
                </Modal.Body>
                {lightbox?.url && (
                    <Modal.Footer className="text-muted small">
                        Captured from:
                        {' '}
                        <code>
                            {lightbox.url}
                        </code>
                    </Modal.Footer>
                )}
            </Modal>

        </Card>

    );

}

function StatCard({ accent, label, muted, value }) {

    return (

        <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center py-3">
                <div
                    style={{
                        color: muted ? '#9ca3af' : (accent || '#1f2937'),
                        fontSize: 32,
                        fontWeight: 600,
                        lineHeight: 1
                    }}
                >
                    {value}
                </div>
                <div className="text-muted mt-1" style={{ fontSize: 12, letterSpacing: 0.3 }}>
                    {label}
                </div>
            </Card.Body>
        </Card>

    );

}

/**
 * Build a structured "why this outcome" object from the run state.
 * Returns { headline, body, tone } where tone maps to a Bootstrap variant.
 *
 * Pattern-matches the terminal status against context the orchestrator gave
 * us (failureReason hint) plus what we can derive from the steps array
 * (last completed action, count of failed/skipped, etc.).
 */
function explainOutcome({ failureReason, runPassed, runSummary, status, steps }) {

    const completed = (steps || []).filter(s => s.status === 'completed' && s.toolName !== 'note');
    const failed = (steps || []).filter(s => s.status === 'failed' && s.toolName && s.toolName !== 'run_failed');
    const lastCompleted = completed[completed.length - 1];
    const lastFailed = failed[failed.length - 1];

    const stepLabel = s => (s ? `${s.toolName}${s.toolInput ? ` ${describeInput(s.toolName, s.toolInput)}` : ''}` : null);

    // 1. Model called finish(passed=true)
    if (status === 'passed') {

        return {
            body: runSummary
                ? 'The AI verified the brief was satisfied — see the detailed summary below.'
                : `The AI completed all checks and called finish with a passing verdict after ${completed.length} action${completed.length === 1 ? '' : 's'}.`,
            headline: 'The AI marked this run as PASSED',
            tone: 'success'
        };

    }

    // 2. Model called finish(passed=false) — this is a "real" test failure (model verdict)
    if (status === 'failed' && runPassed === false && runSummary && !failureReason) {

        return {
            body: 'The AI determined the brief could not be satisfied — see the detailed summary below for what it observed.',
            headline: 'The AI marked this run as FAILED',
            tone: 'danger'
        };

    }

    // 3. Top-level orchestrator failure (Bedrock error, max steps, etc.)
    if (status === 'failed' && failureReason) {

        const reasonText = ({
            bedrock_access: 'Bedrock refused the request — the AWS account may need a use-case form approved or extra permissions to call this model.',
            bedrock_throttled: 'Bedrock throttled the request. Wait a moment and re-run.',
            bedrock_validation: 'Bedrock rejected an outgoing message because of how content was structured. This is a backend bug, not a test failure.',
            max_steps: 'The run hit the safety cap of 100 steps before the AI called finish — most likely an infinite loop or the model couldn\'t reach a verdict.',
            orchestrator_error: 'The orchestrator hit an unexpected error and bailed out of the loop.',
            stuck: 'The AI hit several consecutive failures and a vision check determined the run could not plausibly recover. The reasoning below is what the AI observed.',
            tool_timeout: 'A browser tool call timed out. The iframe may have lost the driver, or the page took longer than the tool budget.',
            viewport_handshake: 'The driver script inside the iframe never responded. Most often this means the target site\'s <script src="…feature-tester-driver.js"> tag is pointing at a URL that returned HTML instead of the JS file (404 page, SPA fallback, deployment mismatch), OR the target site has a Content-Security-Policy that blocks the script. Open DevTools → Network on the iframe and verify the driver URL returns 200 with Content-Type: application/javascript.'
        })[failureReason] || 'The run ended in failure before the AI could call finish.';

        const tail = lastCompleted
            ? ` Last successful step before the failure: ${stepLabel(lastCompleted)}.`
            : ' No actions had completed before the failure.';

        return {
            body: reasonText + tail + (runSummary ? `\n\nUnderlying error: ${runSummary}` : ''),
            headline: 'The run failed before the AI reached a verdict',
            tone: 'danger'
        };

    }

    // 4. Step-level failure that ended the run (rare — usually the loop continues, but possible)
    if (status === 'failed' && lastFailed) {

        return {
            body: `Step ${lastFailed.index} (${stepLabel(lastFailed)}) failed${lastFailed.error ? ` with: ${lastFailed.error}` : ''}.${lastCompleted ? ` Last successful step: ${stepLabel(lastCompleted)}.` : ''}`,
            headline: 'The run ended on a failed step',
            tone: 'danger'
        };

    }

    // 5. WS dropped mid-run
    if (status === 'disconnected') {

        return {
            body: `The browser lost its connection to the run controller mid-run. The iframe driver may have died (a navigation broke it, the page closed, or the tab refreshed). ${lastCompleted ? `Last completed step: ${stepLabel(lastCompleted)}.` : 'No actions had completed before the disconnect.'} ${completed.length} step${completed.length === 1 ? '' : 's'} ran, ${failed.length} failed.`,
            headline: 'The connection dropped before the run finished',
            tone: 'warning'
        };

    }

    // 6. Could never establish the WebSocket
    if (status === 'ws_error') {

        return {
            body: 'Could not establish a WebSocket connection to the backend. Check that the backend server is running and reachable.',
            headline: 'WebSocket error',
            tone: 'warning'
        };

    }

    // 7. User cancelled
    if (status === 'cancelled') {

        return {
            body: `Cancelled by the QA analyst${lastCompleted ? ` after ${stepLabel(lastCompleted)} (${completed.length} action${completed.length === 1 ? '' : 's'} completed).` : '.'}`,
            headline: 'Cancelled',
            tone: 'secondary'
        };

    }

    // 8. The model stopped emitting tool calls without ever calling finish.
    if (status === 'completed') {

        return {
            body: runSummary
                ? 'The AI stopped calling tools and produced this final note — see below.'
                : `The AI stopped calling tools without explicitly marking pass or fail. ${completed.length} action${completed.length === 1 ? '' : 's'} completed.`,
            headline: 'The run completed without an explicit verdict',
            tone: 'info'
        };

    }

    return null;

}

function describeInput(toolName, input) {

    if (!input) return '';
    if (toolName === 'navigate' && input.url) return `→ ${input.url}`;
    if ((toolName === 'click' || toolName === 'type' || toolName === 'wait_for' || toolName === 'query_html' || toolName === 'get_computed_styles') && input.selector)
        return `(${truncate(input.selector, 60)})`;
    if (toolName === 'capture_evidence' && input.label) return `"${truncate(input.label, 60)}"`;
    if (toolName === 'assert_text' && input.text) return `"${truncate(input.text, 60)}"`;
    return '';

}

const OutcomeNarrative = ({ failureReason, runPassed, runSummary, status, steps }) => {

    const explanation = explainOutcome({ failureReason, runPassed, runSummary, status, steps });
    if (!explanation) return null;

    const variant = explanation.tone || 'info';

    return (
        <Card className={`border-0 shadow-sm mb-4 border-start border-4 border-${variant}`}>
            <Card.Body className="px-4 py-3">
                <div className="text-muted small text-uppercase mb-2" style={{ letterSpacing: 0.5 }}>
                    Why this outcome
                </div>
                <div className={`fw-semibold mb-2 text-${variant}`} style={{ fontSize: 16 }}>
                    {explanation.headline}
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {explanation.body}
                </div>
            </Card.Body>
        </Card>
    );

};

export const StepResults = ({
    currentPhaseIndex, currentStepIndex, envContext, evidence, failureReason, freeform, onRunAgain,
    onRunWithChanges, onStartNew, phases, runEndedAt,
    runPassed, runStartedAt,
    runSummary, runUuid, status,
    steps, testPlan, url
}) => {

    const { api } = useApp();

    const [showLog, setShowLog] = useState(true);
    const [showChanges, setShowChanges] = useState(false);
    const [showSave, setShowSave] = useState(false);
    const [showPlaywright, setShowPlaywright] = useState(false);
    const [showDebug, setShowDebug] = useState(false);
    const [debug, setDebug] = useState(null);
    const [debugError, setDebugError] = useState(null);
    const [copied, setCopied] = useState(false);

    // Pull token usage + the matched env primer from the backend so the debug
    // dump is comprehensive without us having to mirror that state client-side.
    useEffect(() => {

        if (!runUuid) return;
        api.get(`/api/runs/${runUuid}/debug`)
            .then(d => setDebug(d || null))
            .catch(e => setDebugError(e?.err || e?.message || String(e)));

    }, [api, runUuid]);

    const debugBundle = formatDebugBundle({
        debug,
        envContext,
        freeform,
        phases,
        runEndedAt,
        runStartedAt,
        runSummary,
        runUuid,
        status,
        steps,
        testPlan,
        url
    });

    const copyDebug = async () => {

        try {

            await navigator.clipboard.writeText(debugBundle);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);

        } catch (_) {

            // ignore — user can still select+copy the textarea content manually
        }

    };

    const theme = STATUS_THEME[status] || STATUS_THEME.completed;
    const duration = formatDuration(runStartedAt, runEndedAt);

    const approved = countByStatus(steps, 'completed');
    const skipped = countByStatus(steps, 'skipped');
    const failed = countByStatus(steps, 'failed');
    const phaseCount = Array.isArray(phases) ? phases.length : 0;

    return (

        <div style={{ flex: 1, overflowY: 'auto' }}>

            {/* Hero */}
            <div
                style={{
                    background: theme.tint,
                    borderBottom: '1px solid var(--bs-border-color)',
                    color: theme.accent,
                    padding: '40px 20px 32px'
                }}
                className="text-center"
            >
                <div
                    style={{
                        fontSize: 56,
                        fontWeight: 700,
                        lineHeight: 1,
                        marginBottom: 8
                    }}
                >
                    {theme.glyph}
                </div>

                <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>
                    {theme.label}
                </h1>

                {duration && (
                    <div style={{ fontSize: 13, marginTop: 6, opacity: 0.75 }}>
                        Run completed in
                        {' '}
                        {duration}
                    </div>
                )}
            </div>

            <Container className="py-4" style={{ maxWidth: 960 }}>

                {/* Outcome narrative — explains WHY the run ended in this
                    state. Combines the model's finish summary (when present)
                    with derived context (last successful step, what failed,
                    and a server-tagged failure reason for non-finish exits). */}
                <OutcomeNarrative
                    failureReason={failureReason}
                    runPassed={runPassed}
                    runSummary={runSummary}
                    status={status}
                    steps={steps}
                />

                {/* Model's finish() prose, separate from the outcome narrative
                    so a passed/failed verdict shows both the "why" header AND
                    the model's full reasoning. Hidden when the summary IS the
                    raw error (status=failed + failureReason set) — that case
                    already shows the error inline in the narrative card. */}
                {runSummary && !failureReason && (
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="px-4 py-3">
                            <div className="text-muted small text-uppercase mb-2" style={{ letterSpacing: 0.5 }}>
                                Detailed summary from the AI
                            </div>
                            <div style={{ fontSize: 15, lineHeight: 1.6 }}>
                                {renderSummary(runSummary)}
                            </div>
                        </Card.Body>
                    </Card>
                )}

                {/* Stats */}
                <Row className="g-3 mb-4">
                    <Col md={3} xs={6}>
                        <StatCard label="Phases" value={phaseCount} />
                    </Col>
                    <Col md={3} xs={6}>
                        <StatCard accent="#16a34a" label="Actions completed" value={approved} />
                    </Col>
                    <Col md={3} xs={6}>
                        <StatCard accent="#f59e0b" label="Skipped" muted={skipped === 0} value={skipped} />
                    </Col>
                    <Col md={3} xs={6}>
                        <StatCard accent="#dc2626" label="Failed" muted={failed === 0} value={failed} />
                    </Col>
                </Row>

                {/* Evidence gallery */}
                <EvidenceGallery evidence={evidence} phases={phases} />

                {/* Phase breakdown / step log */}
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="px-4 py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="text-muted small text-uppercase" style={{ letterSpacing: 0.5 }}>
                                Phase breakdown
                            </div>
                            <Button className="text-decoration-none p-0" onClick={() => setShowLog(v => !v)} size="sm" variant="link">
                                {showLog ? 'Collapse all' : 'Expand all'}
                            </Button>
                        </div>
                        <Collapse in={showLog}>
                            <div className="mt-3">
                                <StepLog
                                    currentPhaseIndex={currentPhaseIndex}
                                    currentStepIndex={currentStepIndex}
                                    phases={phases}
                                    status={status}
                                    steps={steps}
                                />
                            </div>
                        </Collapse>
                    </Card.Body>
                </Card>

                {/* Debug bundle — collapsed by default. Pretty-printed text
                    block that captures everything useful for triage: brief,
                    env primer, every step's tool/input/result/error, token
                    totals. Copyable in one click. */}
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="px-4 py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="text-muted small text-uppercase" style={{ letterSpacing: 0.5 }}>
                                Debug info
                            </div>
                            <div className="d-flex gap-2">
                                {showDebug && (
                                    <Button onClick={copyDebug} size="sm" variant="outline-secondary">
                                        {copied ? '✓ Copied' : 'Copy'}
                                    </Button>
                                )}
                                <Button className="text-decoration-none p-0" onClick={() => setShowDebug(v => !v)} size="sm" variant="link">
                                    {showDebug ? 'Hide' : 'Show'}
                                </Button>
                            </div>
                        </div>
                        <Collapse in={showDebug}>
                            <div className="mt-3">
                                <p className="text-muted small mb-2">
                                    Pretty-printed dump of the run — paste this into a bug report or back to the AI for triage.
                                    {debugError && (
                                    <span className="text-danger ms-2">
                                        (token data unavailable:
                                        {debugError}
                                        )
                                    </span>
                                    )}
                                </p>
                                <Form.Control
                                    readOnly
                                    as="textarea"
                                    onChange={() => {}}
                                    rows={20}
                                    style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                                    value={debugBundle}
                                />
                            </div>
                        </Collapse>
                    </Card.Body>
                </Card>

                {/* Actions */}
                <Card className="border-0 shadow-sm">
                    <Card.Body className="px-4 py-3">
                        <div className="text-muted small text-uppercase mb-3" style={{ letterSpacing: 0.5 }}>
                            What's next?
                        </div>

                        <Row className="g-2">
                            <Col md={6}>
                                <Button className="w-100" onClick={onRunAgain} size="lg" variant="primary">
                                    <span className="me-2">
                                        ↻
                                    </span>
                                    Run again
                                </Button>
                            </Col>
                            <Col md={6}>
                                <Button className="w-100" onClick={() => setShowChanges(true)} size="lg" variant="outline-primary">
                                    Run with changes…
                                </Button>
                            </Col>
                            <Col md={6}>
                                <Button className="w-100" onClick={() => setShowSave(true)} size="lg" variant="outline-secondary">
                                    <span className="me-2">
                                        ↗
                                    </span>
                                    Save as TestRail case…
                                </Button>
                            </Col>
                            <Col md={6}>
                                <Button className="w-100" onClick={() => setShowPlaywright(true)} size="lg" variant="outline-secondary">
                                    <span className="me-2">
                                        ⚙
                                    </span>
                                    Generate Playwright test…
                                </Button>
                            </Col>
                            <Col md={6}>
                                <Button className="w-100" onClick={onStartNew} size="lg" variant="outline-secondary">
                                    <span className="me-2">
                                        +
                                    </span>
                                    Start new test
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

            </Container>

            <RunNotesModal
                onSubmit={notes => {

                    setShowChanges(false); onRunWithChanges(notes);

                }}
                onHide={() => setShowChanges(false)}
                placeholder={'e.g. "the login phase missed verifying the URL after redirect"\n"add a step to log out at the end"'}
                prompt="What should be different next time? Notes go to the planner so it can re-organize the phases. Optional."
                show={showChanges}
                submitLabel="Regenerate phases"
                title="Run with changes"
            />

            <SaveToTestRailModal
                onHide={() => setShowSave(false)}
                phases={phases}
                runSummary={runSummary}
                show={showSave}
                testPlan={testPlan}
                url={url}
            />

            <PlaywrightExportModal
                onHide={() => setShowPlaywright(false)}
                runUuid={runUuid}
                show={showPlaywright}
            />

        </div>

    );

};
